// The real Monaco-based HollowScript editing surface, replacing the plain
// `<textarea>` both `DockConsole` (tier 1, `editor-panels.tsx`) and
// `PlayScriptConsole` (tier 2, `PlayScriptConsole.tsx`) used before: live
// syntax highlighting (`monacoAdapter.ts`'s tokens provider, driven by the
// real `highlight()` lexer), inline squiggly diagnostics from the real
// `check()`, capability-surface + variable-scope completion, and
// docs-on-hover - Roblox Studio's Luau editor is the UX bar named in
// docs/editor-consolidation/roadmap.md section 3.4/task item 5.
//
// `monaco-editor` (~98MB unpacked) is dynamically imported inside this
// component's own mount effect, not at module scope - so importing this
// file's default export costs nothing until an instance actually mounts,
// and both call sites already only mount this component once their own
// console surface is opened (`DockConsole` renders unconditionally once the
// dock's "Console" tab is open; `PlayScriptConsole` returns `null` before
// its `open` guard) - the same "pay only when a script surface actually
// opens" discipline `hollowScriptRuntime.ts`'s own dynamic import already
// applies to the Worker + `@hollow/script` runtime.
//
// Deliberately not using `@monaco-editor/react` - see `monacoSetup.ts`'s own
// doc comment for why that package is currently incompatible with a current
// `monaco-editor`. Mounting `monaco-editor` directly is the standard,
// well-documented pattern (`monaco.editor.create` in a mount effect,
// disposed on unmount) and is what this component does.

import { useEffect, useRef, useState } from 'react';
import type * as Monaco from 'monaco-editor';
import type { StudioCapabilityName } from '../../../../engine/scripting/capabilities.js';
import { ensureMonacoWorkerConfigured } from './monacoSetup.js';
import { HOLLOWSCRIPT_LANGUAGE_ID, registerHollowScriptLanguage, setActiveHollowScriptCapabilities, updateHollowScriptMarkers } from './monacoAdapter.js';

const DIAGNOSTICS_DEBOUNCE_MS = 300;

export interface HollowScriptEditorProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Fired on a plain Enter (no Shift, and only when neither the suggestion
   * nor find widget is open) - matches the textarea-based console's own
   * `onKeyDown` rule (`if (e.key === 'Enter' && !e.shiftKey) run()`) so
   * swapping the input surface changes nothing about run/output/history
   * behaviour. Shift+Enter keeps Monaco's own default (insert a newline). */
  readonly onRunRequested?: () => void;
  /** `undefined` grants every v1 capability, matching `runScript`'s own
   * default posture (`narrowScriptCapabilities`) - drives both live
   * diagnostics and the completion/hover providers. */
  readonly capabilities: ReadonlySet<StudioCapabilityName> | undefined;
  readonly theme: 'light' | 'dark';
  readonly readOnly?: boolean;
  readonly autoFocus?: boolean;
  readonly placeholder?: string;
  /** CSS height of the editor's own scroll region - the resize handle below
   * it lets a creator drag it taller, matching the old `<textarea rows={2}
   * style={{ resize: 'vertical' }}>`. */
  readonly height?: number;
}

/** A HollowScript source editor sized and chromed for a compact console
 * input (no line numbers/minimap/folding - a REPL line or a short script,
 * not a full-file IDE view), not a general-purpose code editor. */
export function HollowScriptEditor({
  value,
  onChange,
  onRunRequested,
  capabilities,
  theme,
  readOnly = false,
  autoFocus = false,
  placeholder,
  height = 96,
}: HollowScriptEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const onChangeRef = useRef(onChange);
  const onRunRequestedRef = useRef(onRunRequested);
  const capabilitiesRef = useRef(capabilities);
  const markerTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [ready, setReady] = useState(false);

  onChangeRef.current = onChange;
  onRunRequestedRef.current = onRunRequested;
  capabilitiesRef.current = capabilities;

  // Mount once: creates the editor, wires change/run/marker handling, and
  // tears everything down on unmount. Every value that can change later
  // (value/theme/readOnly/capabilities) is applied by its own effect below
  // via `editorRef`/`monacoRef`, not by re-running this one - recreating a
  // live Monaco editor on every keystroke would drop undo history, cursor
  // position and scroll state.
  useEffect(() => {
    let disposed = false;
    let editor: Monaco.editor.IStandaloneCodeEditor | undefined;
    let model: Monaco.editor.ITextModel | undefined;
    const restoreCapabilities = setActiveHollowScriptCapabilities(() => capabilitiesRef.current);

    void (async () => {
      ensureMonacoWorkerConfigured();
      const monaco = await import('monaco-editor');
      if (disposed || !containerRef.current) return;
      monacoRef.current = monaco;
      registerHollowScriptLanguage(monaco);

      model = monaco.editor.createModel(value, HOLLOWSCRIPT_LANGUAGE_ID);
      editor = monaco.editor.create(containerRef.current, {
        model,
        readOnly,
        theme: theme === 'dark' ? 'hollowscript-dark' : 'hollowscript-light',
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 12.5,
        lineHeight: 20,
        lineNumbers: 'off',
        glyphMargin: false,
        folding: false,
        minimap: { enabled: false },
        overviewRulerLanes: 0,
        hideCursorInOverviewRuler: true,
        renderLineHighlight: 'none',
        scrollBeyondLastLine: false,
        automaticLayout: true,
        wordWrap: 'on',
        tabSize: 2,
        scrollbar: { alwaysConsumeMouseWheel: false },
        padding: { top: 8, bottom: 8 },
        fixedOverflowWidgets: true,
      });
      editorRef.current = editor;

      editor.onDidChangeModelContent(() => {
        const next = model!.getValue();
        onChangeRef.current(next);
        if (markerTimeoutRef.current !== undefined) clearTimeout(markerTimeoutRef.current);
        markerTimeoutRef.current = setTimeout(() => {
          updateHollowScriptMarkers(monaco, model!, capabilitiesRef.current);
        }, DIAGNOSTICS_DEBOUNCE_MS);
      });

      // Plain Enter runs the program (matching the textarea's own
      // onKeyDown rule); Shift+Enter keeps Monaco's default newline.
      // Suppressed while the suggestion/find widgets are open so accepting
      // a completion or confirming a find still works normally.
      editor.addCommand(
        monaco.KeyCode.Enter,
        () => onRunRequestedRef.current?.(),
        '!suggestWidgetVisible && !findWidgetVisible && !renameInputVisible',
      );

      if (autoFocus) editor.focus();
      updateHollowScriptMarkers(monaco, model, capabilitiesRef.current);
      setReady(true);
    })();

    return () => {
      disposed = true;
      restoreCapabilities();
      if (markerTimeoutRef.current !== undefined) clearTimeout(markerTimeoutRef.current);
      editor?.dispose();
      model?.dispose();
      editorRef.current = null;
    };
    // Mount-once by design - see the effect's own comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync an externally-driven `value` (e.g. DockConsole clearing the input
  // after a run) into the model when it didn't originate from this editor's
  // own `onDidChangeModelContent`.
  useEffect(() => {
    const editor = editorRef.current;
    const model = editor?.getModel();
    if (!model || model.getValue() === value) return;
    model.setValue(value);
  }, [value]);

  useEffect(() => {
    editorRef.current?.updateOptions({ readOnly });
  }, [readOnly]);

  useEffect(() => {
    monacoRef.current?.editor.setTheme(theme === 'dark' ? 'hollowscript-dark' : 'hollowscript-light');
  }, [theme]);

  // A capability grant change must reach the very next keystroke's
  // completion list (`monacoAdapter.ts`'s own doc comment) and should
  // re-flag/clear diagnostics immediately, not wait for the next edit.
  useEffect(() => {
    const monaco = monacoRef.current;
    const model = editorRef.current?.getModel();
    if (!monaco || !model) return;
    updateHollowScriptMarkers(monaco, model, capabilities);
  }, [capabilities, ready]);

  return (
    <div style={{ position: 'relative', height, resize: 'vertical', overflow: 'hidden', minHeight: 44 }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {!ready ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'var(--ss-mono)',
            fontSize: 11.5,
            color: 'var(--ss-text-3)',
            padding: '0 12px',
            pointerEvents: 'none',
          }}
        >
          Loading editor…
        </div>
      ) : null}
      {ready && value === '' && placeholder ? (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 12,
            fontFamily: 'var(--ss-mono)',
            fontSize: 12.5,
            color: 'var(--ss-text-3)',
            pointerEvents: 'none',
          }}
        >
          {placeholder}
        </div>
      ) : null}
    </div>
  );
}
